"use client"

import { Suspense, lazy, useCallback, useEffect, type ReactNode } from "react"

import { usePathname, useSearchParams } from "next/navigation"

import { SideSheet } from "@/components/flow/SideSheet"
import { useInlineSignup } from "@/components/flow/SignupEntry"
import { AuthLoading } from "@/features/auth/AuthLoading"
import { CHECKOUT_SOURCE } from "@/features/auth/lib/checkout"
import { useLocalizedReplace, useShallowQueryParams } from "@/lib/hooks/use-navigation"
import { buildRoute, ROUTES } from "@/lib/routes"
import { removeDatadogGlobalProperty, setDatadogGlobalProperty } from "@/platform/observability/datadog/context"

const SignupSheet = lazy(() => import("@/features/checkout/signup/components/SignupSheet").then((module) => ({ default: module.SignupSheet })))

export const InlineSignupSheet = ({ waiting, rejected }: { waiting: ReactNode; rejected: ReactNode }) => {
  const enabled = useInlineSignup()
  const searchParams = useSearchParams()
  const updateQuery = useShallowQueryParams()
  const pathname = usePathname()
  const replace = useLocalizedReplace()
  const productId = searchParams.get("productId")
  const variantId = searchParams.get("variantId")
  const previewFlag = searchParams.get("preview") === "1"
  const source = searchParams.get("source")
  const redirectsToCheckout = searchParams.get("signup") === "1" && Boolean(productId && variantId)
  useEffect(() => {
    if (!redirectsToCheckout || !productId || !variantId) return
    const segments = pathname.split("/")
    const previewHandle = previewFlag ? segments[segments.indexOf(ROUTES.PRODUCTS) + 1] : undefined
    replace(buildRoute.checkout({ productId, variantId, previewHandle, source: source ?? undefined }))
  }, [redirectsToCheckout, productId, variantId, previewFlag, source, pathname, replace])
  const open = enabled && searchParams.get("signup") === "1" && !productId && !variantId
  const close = useCallback(() => updateQuery({ signup: null, preview: null }, { replace: true }), [updateQuery])
  useEffect(() => {
    if (!open) return
    setDatadogGlobalProperty("checkout_source", CHECKOUT_SOURCE.MARKETPLACE_WEB_SIGNUP)
    return () => {
      removeDatadogGlobalProperty("checkout_source")
      removeDatadogGlobalProperty("user_cohort")
    }
  }, [open])
  if (!open) return null
  return (
    <SideSheet isOpen={open} close={close}>
      <div data-testid="signup-sheet">
        <Suspense fallback={<AuthLoading />}>
          <SignupSheet onClose={close} waiting={waiting} rejected={rejected} embedded />
        </Suspense>
      </div>
    </SideSheet>
  )
}
